import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SupportChatService } from 'src/app/private/services/support-chat.service';
import {
  WebChatAttachmentRef,
  WebChatParsedFrame,
  parseWebLiveChatWebSocketFrame,
  websocketMessageDataAsString
} from 'src/app/private/utils/web-live-chat-ws.util';
import {
  LIVE_CHAT_SESSION_ENDED_MESSAGE,
  liveChatResponseMessage,
  liveChatUserMessage
} from 'src/app/private/utils/live-chat-user-message';

type ChatPhase = 'form' | 'idle' | 'starting' | 'live' | 'ended' | 'error';

interface UiBubble {
  id: string;
  role: string;
  displayName?: string;
  text: string;
  attachments: WebChatAttachmentRef[];
}

function getVisitorId(): string {
  const key = 'chat_embed_visitor_id';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = 'visitor-' + crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
}

/** Merge `?q` from `location.search` and from `location.hash` (e.g. `#/chat-embed?…`). */
function readUrlQueryParams(): URLSearchParams {
  const chunks: string[] = [];
  const s = window.location.search;
  if (s && s.startsWith('?')) {
    chunks.push(s.slice(1));
  }
  const hash = window.location.hash || '';
  const qi = hash.indexOf('?');
  if (qi !== -1) {
    chunks.push(hash.slice(qi + 1));
  }
  return new URLSearchParams(chunks.join('&'));
}

@Component({
  selector: 'app-chat-embed',
  templateUrl: './chat-embed.component.html',
  styleUrls: ['./chat-embed.component.scss']
})
export class ChatEmbedComponent implements OnInit, OnDestroy {

  // ── Chat state ────────────────────────────────────────────────────────────
  isOpen   = false;
  phase: ChatPhase = 'form';
  draft    = '';
  uiMessages: UiBubble[] = [];
  sending  = false;
  uploading = false;
  ending   = false;
  statusText = '';
  statusLive = false;
  sessionId: string | null = null;
  private wsToken = '';
  private lastMessageUlid = '';

  // ── Pre-chat form ─────────────────────────────────────────────────────────
  formHasCustomerId = true;
  formCustomerId = '';
  formName = '';
  formPhone = '';
  formEmail = '';
  formSubject    = '';
  formError      = '';
  formSubmitting = false;

  private embedToken  = '';
  private visitorId   = '';
  private clientId    = '';
  private customerId   = '';
  private customerName = '';
  private leadId: number | null = null;
  private chatSubject = '';
  private agentName   = '';

  private ws: WebSocket | null = null;
  private intentionalWsClose = false;
  private readonly pollFallbackMs = 5000;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  // ── Floating widget position & size ──────────────────────────────────────
  pos  = { x: 0, y: 0 };
  size = { w: 360, h: 520 };

  private _drag: { on: boolean; sx: number; sy: number; ox: number; oy: number } =
    { on: false, sx: 0, sy: 0, ox: 0, oy: 0 };

  private _resize: { on: boolean; sx: number; sy: number; ow: number; oh: number } =
    { on: false, sx: 0, sy: 0, ow: 0, oh: 0 };

  constructor(
    private route: ActivatedRoute,
    private chat: SupportChatService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    // App uses hash routing (`useHash: true`). Params live after `?` in the hash, e.g. `#/chat-embed?embed_token=…`.
    // Also merge `location.search` for edge cases and read ActivatedRoute snapshot.
    const urlParams = readUrlQueryParams();

    this.embedToken = (
      this.route.snapshot.queryParamMap.get('embed_token') ||
      urlParams.get('embed_token') ||
      ''
    ).trim();

    // Use the logged-in user_id when provided (internal support page).
    // Fall back to a session-scoped visitor UUID for external/public embeds.
    const urlUserId =
      this.route.snapshot.queryParamMap.get('user_id') ||
      urlParams.get('user_id') ||
      '';
    this.visitorId = urlUserId || getVisitorId();

    this.clientId = (
      this.route.snapshot.queryParamMap.get('client_id') ||
      urlParams.get('client_id') ||
      ''
    ).trim();

    this.phase = 'form';

    // Position widget at bottom-right corner initially
    this.pos = {
      x: window.innerWidth  - this.size.w - 20,
      y: window.innerHeight - this.size.h - 20,
    };

    // Make the iframe background transparent so only the widget is visible
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';

    this.notifyParentBounds();
  }

  ngOnDestroy(): void {
    this.stopPollFallback();
    this.closeWsOnly();
    document.documentElement.style.background = '';
    document.body.style.background = '';
  }

  // ── Drag ─────────────────────────────────────────────────────────────────
  onDragStart(e: MouseEvent): void {
    e.preventDefault();
    this._drag = { on: true, sx: e.clientX, sy: e.clientY, ox: this.pos.x, oy: this.pos.y };
  }
  onDragTouchStart(e: TouchEvent): void {
    const t = e.touches[0];
    this._drag = { on: true, sx: t.clientX, sy: t.clientY, ox: this.pos.x, oy: this.pos.y };
  }

  // ── Resize ───────────────────────────────────────────────────────────────
  onResizeStart(e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this._resize = { on: true, sx: e.clientX, sy: e.clientY, ow: this.size.w, oh: this.size.h };
  }
  onResizeTouchStart(e: TouchEvent): void {
    e.stopPropagation();
    const t = e.touches[0];
    this._resize = { on: true, sx: t.clientX, sy: t.clientY, ow: this.size.w, oh: this.size.h };
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    if (this._drag.on) {
      this.pos = {
        x: Math.max(0, Math.min(window.innerWidth  - this.size.w, this._drag.ox + e.clientX - this._drag.sx)),
        y: Math.max(0, Math.min(window.innerHeight - this.size.h, this._drag.oy + e.clientY - this._drag.sy)),
      };
      this.notifyParentBounds();
    }
    if (this._resize.on) {
      this.size = {
        w: Math.max(280, this._resize.ow + e.clientX - this._resize.sx),
        h: Math.max(300, this._resize.oh + e.clientY - this._resize.sy),
      };
      this.notifyParentBounds();
    }
  }

  @HostListener('document:mouseup')
  onMouseUp(): void { this._drag.on = false; this._resize.on = false; }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(e: TouchEvent): void {
    const t = e.touches[0];
    if (this._drag.on) {
      this.pos = {
        x: Math.max(0, Math.min(window.innerWidth  - this.size.w, this._drag.ox + t.clientX - this._drag.sx)),
        y: Math.max(0, Math.min(window.innerHeight - this.size.h, this._drag.oy + t.clientY - this._drag.sy)),
      };
      this.notifyParentBounds();
    }
    if (this._resize.on) {
      this.size = {
        w: Math.max(280, this._resize.ow + t.clientX - this._resize.sx),
        h: Math.max(300, this._resize.oh + t.clientY - this._resize.sy),
      };
      this.notifyParentBounds();
    }
  }

  @HostListener('document:touchend')
  onTouchEnd(): void { this._drag.on = false; this._resize.on = false; }

  // ── UI helpers ────────────────────────────────────────────────────────────
  setOpen(value: boolean): void {
    this.isOpen = value;
    this.notifyParentBounds();
  }

  trackById(_i: number, m: UiBubble): string { return m.id; }

  bubbleClass(role: string): string {
    const r = role.toUpperCase();
    if (r === 'CUSTOMER') return 'ce-bubble-wrap ce-bubble-customer';
    if (r === 'SYSTEM')   return 'ce-bubble-wrap ce-bubble-system';
    return 'ce-bubble-wrap ce-bubble-agent';
  }

  labelForRole(role: string, displayName?: string): string {
    const r = role.toUpperCase();
    if (r === 'CUSTOMER') return 'You';
    if (r === 'AGENT')    return displayName?.trim() || 'Agent';
    if (r === 'SYSTEM')   return 'System';
    return displayName?.trim() || role;
  }

  canCompose(): boolean { return this.phase === 'live' && !!this.sessionId; }

  setHasCustomerId(value: boolean): void {
    this.formHasCustomerId = value;
    this.formError = '';
  }

  // ── Chat actions ──────────────────────────────────────────────────────────
  submitPreChatForm(): void {
    this.formError = '';
    const subject = this.formSubject.trim();
    if (!subject) {
      this.formError = 'Please enter a subject for this chat.';
      return;
    }

    if (this.formHasCustomerId) {
      this.submitPreChatWithCustomerId(subject);
      return;
    }
    this.submitPreChatWithoutCustomerId(subject);
  }

  private submitPreChatWithCustomerId(subject: string): void {
    const customerId = this.formCustomerId.trim();
    if (!customerId) {
      this.formError = 'Please enter the customer ID.';
      return;
    }
    if (!this.clientId && !this.embedToken) {
      this.formError = 'Live chat is not configured for this account (missing embed token).';
      return;
    }

    this.formSubmitting = true;
    this.chat.getLeadIdByCustomerId({
      customer_id: customerId,
      ...(this.clientId ? { client_id: this.clientId } : { embed_token: this.embedToken }),
    }).subscribe({
      next: (res) => {
        this.formSubmitting = false;
        if (!res?.lead_id) {
          this.formError = 'No lead found for this customer ID. Check the ID and try again.';
          return;
        }
        this.customerId = customerId;
        this.customerName = String(res.contact_name || '').trim();
        this.leadId = Number(res.lead_id);
        this.chatSubject = subject;
        this.phase = 'idle';
      },
      error: (err: unknown) => {
        this.formSubmitting = false;
        this.formError = liveChatUserMessage(err, 'Could not verify customer ID. Please try again.');
      }
    });
  }

  private submitPreChatWithoutCustomerId(subject: string): void {
    const contactName = this.formName.trim();
    const contactPhone = this.formPhone.trim();
    const contactEmail = this.formEmail.trim();

    if (!contactName) {
      this.formError = 'Please enter your name.';
      return;
    }
    if (!contactEmail) {
      this.formError = 'Please enter your email address.';
      return;
    }
    if (!this.embedToken) {
      this.formError = 'Live chat is not set up on this page. Please contact support.';
      return;
    }

    this.formSubmitting = true;
    this.chat.createLiveChatLead({
      embed_token: this.embedToken,
      contact_name: contactName,
      ...(contactPhone ? { contact_phone: contactPhone } : {}),
      contact_email: contactEmail,
    }).subscribe({
      next: (res) => {
        this.formSubmitting = false;
        if (!res?.lead_id || !res?.customer_id) {
          this.formError = 'Could not register your details. Please try again.';
          return;
        }
        this.customerId = String(res.customer_id);
        this.customerName = String(res.contact_name || contactName).trim();
        this.leadId = Number(res.lead_id);
        this.chatSubject = subject;
        this.phase = 'idle';
      },
      error: (err: unknown) => {
        this.formSubmitting = false;
        this.formError = liveChatUserMessage(err, 'Could not register your details. Please try again.');
      }
    });
  }

  startChat(): void {
    if (!this.embedToken) {
      this.appendSystem(
        'Live chat is not set up on this page. Ask the site owner to add your chat link, or open the chat from your CRM after signing in.'
      );
      this.phase = 'error';
      return;
    }
    if (!this.customerId || !this.chatSubject) {
      this.phase = 'form';
      this.formError = 'Customer ID and subject are required before starting chat.';
      return;
    }
    this.phase = 'starting';
    this.uiMessages = [];
    this.sessionId = null;
    this.agentName = '';
    this.closeWsOnly();
    this.setStatus('Connecting…', false);

    const chatDisplayName =
      this.customerName || `Customer ${this.customerId}`;

    this.chat.startConnectChat({
      embed_token: this.embedToken,
      user_id: this.visitorId,
      displayName: chatDisplayName,
      email: this.formEmail.trim() || undefined,
      customer_id: this.customerId,
      lead_id: this.leadId ?? undefined,
      subject: this.chatSubject,
    }).subscribe({
      next: (res) => {
        if (!res?.sessionId || !res?.websocketUrl) {
          this.appendSystem(
            liveChatResponseMessage(res, 'We could not start the chat. Please try again in a moment.')
          );
          this.phase = 'error';
          this.setStatus('Offline', false);
          return;
        }
        this.sessionId = res.sessionId;
        this.wsToken = res.ws_token || '';
        this.phase = 'live';
        this.openWs(res.websocketUrl || '');
        this.startPollFallback();
        this.appendSystem('Connected. An agent will join shortly.');
        this.setStatus('Connected', true);
      },
      error: (err: unknown) => {
        this.appendSystem(liveChatUserMessage(err, 'We could not start the chat. Please try again.'));
        this.phase = 'error';
        this.setStatus('Offline', false);
      }
    });
  }

  endChat(): void {
    if (!this.sessionId || this.ending) return;
    this.finishChatSession(true);
  }

  startNewConversation(): void {
    this.phase = 'form';
    this.uiMessages = [];
    this.sessionId = null;
    this.agentName = '';
    this.formHasCustomerId = true;
    this.formCustomerId = '';
    this.formName = '';
    this.formPhone = '';
    this.formEmail = '';
    this.formSubject = '';
    this.customerId = '';
    this.customerName = '';
    this.leadId = null;
    this.chatSubject = '';
    this.setStatus('', false);
  }

  send(): void {
    const text = this.draft.trim();
    if (!text || !this.sessionId || this.sending) return;
    this.sending = true;

    this.chat.sendConnectChatMessage({
      embed_token: this.embedToken,
      user_id: this.visitorId,
      sessionId: this.sessionId,
      content: text
    }).subscribe({
      next: () => {
        this.appendBubble('CUSTOMER', text, []);
        this.draft = '';
        this.sending = false;
      },
      error: (err: unknown) => {
        this.sending = false;
        this.appendSystem(liveChatUserMessage(err, 'Your message could not be sent. Please try again.'));
      }
    });
  }

  onEnter(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send(); }
  }

  onFileSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file  = input?.files?.[0];
    if (!file || !this.sessionId || this.uploading) { if (input) input.value = ''; return; }

    this.uploading = true;
    this.setStatus('Uploading…', true);

    this.chat.uploadConnectChatAttachment({
      embed_token: this.embedToken,
      sessionId: this.sessionId!,
      file,
    }).subscribe({
      next: (r) => {
        if (!r?.ok || !r.attachment) {
          this.uploading = false;
          this.setStatus('Connected', true);
          if (input) input.value = '';
          this.appendSystem(
            liveChatResponseMessage(r, 'That file could not be sent. Try a smaller file or a different format.')
          );
          return;
        }
        const att = r.attachment;
        this.chat.sendConnectChatMessage({
          embed_token: this.embedToken,
          user_id: this.visitorId,
          sessionId: this.sessionId!,
          content: `Sent file: ${att.name}`,
          attachments: [{ key: att.object_key, name: att.name, mime: att.mime, size: att.size }],
        }).subscribe({
          next: () => {
            this.uploading = false;
            this.setStatus('Connected', true);
            if (input) input.value = '';
            this.appendBubble('CUSTOMER', `Sent file: ${att.name}`, [{ key: att.object_key, name: att.name, mime: att.mime }]);
          },
          error: (err: unknown) => {
            this.uploading = false;
            this.setStatus('Connected', true);
            if (input) input.value = '';
            this.appendSystem(liveChatUserMessage(err, 'File uploaded but message could not be sent.'));
          }
        });
      },
      error: (err: unknown) => {
        this.uploading = false;
        this.setStatus('Connected', true);
        if (input) input.value = '';
        this.appendSystem(liveChatUserMessage(err, 'That file could not be uploaded. Please try again.'));
      }
    });
  }

  // ── Private helpers ───────────────────────────────────────────────────────
  private setStatus(text: string, live: boolean): void { this.statusText = text; this.statusLive = live; }

  private notifyParentBounds(): void {
    if (window.parent === window) return;
    const bounds = this.isOpen
      ? { x: this.pos.x, y: this.pos.y, w: this.size.w, h: this.size.h }
      : { x: window.innerWidth - 150, y: window.innerHeight - 72, w: 150, h: 52 };
    try { window.parent.postMessage({ type: 'ce_bounds', ...bounds }, '*'); } catch { /* cross-origin */ }
  }

  private appendSystem(text: string): void { this.appendBubble('SYSTEM', text, []); }

  private appendBubble(role: string, text: string, attachments: WebChatAttachmentRef[], displayName?: string, messageId?: string): void {
    if (messageId) this.lastMessageUlid = messageId;
    this.uiMessages = [...this.uiMessages, {
      id: messageId || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      role, displayName, text, attachments: attachments || []
    }];
  }

  private applyFrame(parsed: WebChatParsedFrame): void {
    if (parsed.role === 'CUSTOMER') return;
    if (parsed.messageId) this.lastMessageUlid = parsed.messageId;
    if (parsed.role === 'AGENT' && parsed.displayName?.trim()) {
      this.agentName = parsed.displayName.trim();
    }
    if (parsed.sessionEnded) {
      this.appendSystem(LIVE_CHAT_SESSION_ENDED_MESSAGE);
      this.finishChatSession(false);
      return;
    }
    this.appendBubble(parsed.role, parsed.text, parsed.attachments, parsed.displayName, parsed.messageId);
  }

  private finishChatSession(userInitiated: boolean): void {
    const sessionId = this.sessionId;
    if (!sessionId) return;

    this.ending = true;
    this.stopPollFallback();
    this.intentionalWsClose = true;
    this.closeWsOnly();

    this.chat.endConnectChat({
      embed_token: this.embedToken,
      user_id: this.visitorId,
      sessionId,
      agent_name: this.agentName || undefined
    }).subscribe({
      next: () => {
        this.ending = false;
        if (userInitiated) {
          this.appendSystem('You ended the chat.');
        }
        this.phase = 'ended';
        this.sessionId = null;
        this.setStatus('Ended', false);
        this.notifyParentBounds();
      },
      error: (err: unknown) => {
        this.ending = false;
        this.sessionId = null;
        this.phase = 'ended';
        this.setStatus('Ended', false);
        this.notifyParentBounds();
        if (userInitiated) {
          this.toastr.error(liveChatUserMessage(err, 'We could not end the chat cleanly. The chat may already be closed.'));
        }
      }
    });
  }

  private openWs(url: string): void {
    if (!url) return;
    this.closeWsOnly();
    this.intentionalWsClose = false;
    const socket = new WebSocket(url);
    this.ws = socket;

    socket.addEventListener('open', () => {
      this.setStatus('Connected', true);
    });

    socket.addEventListener('message', (ev) => {
      void (async () => {
        let raw: string;
        try { raw = await websocketMessageDataAsString(ev); } catch { return; }
        const parsed = parseWebLiveChatWebSocketFrame(raw);
        if (parsed) this.applyFrame(parsed);
      })();
    });

    socket.addEventListener('close', () => {
      if (this.intentionalWsClose) { this.intentionalWsClose = false; return; }
      if (this.sessionId && this.phase === 'live') {
        this.setStatus('Reconnecting…', false);
        this.resumeMissedMessages(() => this.openWs(url));
      }
    });

    socket.addEventListener('error', () => this.setStatus('Connection problem', false));
  }

  private resumeMissedMessages(onDone?: () => void): void {
    if (!this.sessionId) return;
    this.chat.getWebChatMessages({
      embed_token: this.embedToken,
      session_id: this.sessionId,
      after_ulid: this.lastMessageUlid || undefined,
      limit: 100,
    }).subscribe({
      next: (res) => {
        for (const item of res?.items || []) {
          const role = String(item.sender_type || '').toUpperCase();
          if (role === 'CUSTOMER') continue;
          this.appendBubble(role, item.content || '', item.attachments || [], item.sender_name, item.message_id);
        }
        onDone?.();
      },
      error: () => onDone?.(),
    });
  }

  private startPollFallback(): void {
    this.stopPollFallback();
    this.pollTimer = setInterval(() => {
      if (this.phase === 'live' && this.sessionId) this.resumeMissedMessages();
    }, this.pollFallbackMs);
  }

  private stopPollFallback(): void {
    if (this.pollTimer != null) { clearInterval(this.pollTimer); this.pollTimer = null; }
  }

  private closeWsOnly(): void {
    this.stopPollFallback();
    if (this.ws) { this.intentionalWsClose = true; try { this.ws.close(); } catch { /* ignore */ } this.ws = null; }
  }
}
